'use client'

import * as React from 'react'
import * as Sentry from '@sentry/react'
import type { RegistrationSource } from '@repo/auth-utils'
import type { UserAuthResponse } from '@repo/cf-user/types/auth'
import type { AuthModalLocations } from '@repo/analytics/amplitude/utils'

export const REGISTRATION_LOGIN_SUCCESS_EVENT_NAME = 'cf_registration_login_success'
export type Step = 'login' | 'login-otp' | 'register' | 'register-otp' | 'forget-password'
export interface GoogleAdsTrackingOptions {
  registrationConversion?: boolean // Sends the registration conversion event to Google Ads
}

type SuccessCallback = (me: UserAuthResponse) => void

type OpenModalCallback = (open: boolean) => void

/**
 * Platform-specific token persistence. When provided, it replaces the default
 * cookie-bridge persistence in the auth flow (e.g. Studio Desktop stores the
 * JWT in localStorage and hands it to the Electron main process).
 */
export type PersistAuthToken = (
  token: string,
  origin: 'login' | 'registration',
) => void | Promise<void>

interface OpenModalOptions {
  successCallback?: SuccessCallback
  modalOpenChangeCallback?: OpenModalCallback
  location?: AuthModalLocations
}

interface UserAuthContextModel {
  isOpen: boolean
  setIsOpen(open: boolean): void
  openModal(step?: Step, options?: OpenModalOptions): void
  closeModal(): void
  step: Step
  setStep(step: Step): void
  formState: FormData | null
  setFormState(formData: FormData): void
  errorMessage: React.ReactNode | null
  setErrorMessage(message: React.ReactNode | null): void
  successMessage: React.ReactNode | null
  setSuccessMessage(message: React.ReactNode | null): void
  //Function to be executed after successful login or registration
  successCallback?: React.MutableRefObject<SuccessCallback | null>
  modalOpenChangeCallback: React.MutableRefObject<OpenModalCallback | null>
  trackingOptions?: GoogleAdsTrackingOptions
  skipReloadPageOnSuccess?: boolean
  persistAuthToken?: PersistAuthToken
  registrationSource?: RegistrationSource
}

export interface UserAuthModalProviderProps {
  children: React.ReactNode
  trackingOptions?: GoogleAdsTrackingOptions
  skipReloadPageOnSuccess?: boolean // If true, the page will not be reloaded after successful login or registration
  persistAuthToken?: PersistAuthToken
  registrationSource?: RegistrationSource
  onModalOpen?: (step: Step, location?: AuthModalLocations) => void
}

export const UserAuthModalContext = React.createContext<UserAuthContextModel | null>(null)

export const UserAuthModalProvider = ({
  children,
  trackingOptions,
  skipReloadPageOnSuccess = false,
  persistAuthToken,
  registrationSource,
  onModalOpen,
}: UserAuthModalProviderProps) => {
  const [errorMessage, setErrorMessage] = React.useState<React.ReactNode | null>(null)
  const [successMessage, setSuccessMessage] = React.useState<React.ReactNode | null>(null)
  const [isOpen, setIsOpen] = React.useState(false)
  const [step, setStep] = React.useState<Step>('register')
  const [formState, setFormState] = React.useState<FormData | null>(null)
  const successCallbackRef = React.useRef<SuccessCallback | null>(null)
  const onModalOpenChangeCallbackRef = React.useRef<OpenModalCallback | null>(null)

  const openModal = React.useCallback(
    (step: Step = 'register', options?: OpenModalOptions) => {
      const { successCallback, modalOpenChangeCallback, location } = options || {}

      setStep(step)

      successCallbackRef.current = successCallback || null
      onModalOpenChangeCallbackRef.current = modalOpenChangeCallback || null

      setIsOpen(true)
      onModalOpen?.(step, location)

      Sentry.addBreadcrumb({
        category: 'user-auth-modal',
        message: 'User auth modal opened',
        level: 'info',
        data: {
          step,
        },
      })
    },
    [onModalOpen],
  )

  const closeModal = () => {
    Sentry.addBreadcrumb({
      category: 'user-auth-modal',
      message: 'User auth modal closed',
      level: 'info',
    })
    setIsOpen(false)
  }

  const handleStepChange = (step: Step) => {
    setStep(step)
    setErrorMessage(null)
    setSuccessMessage(null)
    Sentry.addBreadcrumb({
      category: 'user-auth-modal',
      message: 'User auth modal step changed',
      level: 'info',
      data: {
        step,
      },
    })
  }

  return (
    <UserAuthModalContext.Provider
      value={{
        isOpen,
        setIsOpen,
        openModal,
        closeModal,
        step,
        setStep: handleStepChange,
        formState,
        setFormState,
        errorMessage,
        setErrorMessage,
        successMessage,
        setSuccessMessage,
        successCallback: successCallbackRef,
        modalOpenChangeCallback: onModalOpenChangeCallbackRef,
        trackingOptions,
        skipReloadPageOnSuccess,
        persistAuthToken,
        registrationSource,
      }}
    >
      {children}
    </UserAuthModalContext.Provider>
  )
}

export function useUserAuthModal() {
  const context = React.useContext(UserAuthModalContext)

  if (!context) {
    throw new Error('useUserAuthModal must be used within a UserAuthModalContext')
  }

  return context
}
